import React, { useEffect, useRef, useState } from 'react';
import { capitalize, chunk, isEmpty, startCase } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    doFileUploadEntry,
    getDocumentDetailsByID,
    getFileSignedUrl,
    getFileUploadsByDocumentRequestID,
    popup,
    validateFiles,
    signedURL,
    updateDocsRequest,
    deleteDocsRequest,
    getContentTypeByExtension,
    handleScroll,
    getFileExtension,
    isDocORExcelExtension,
} from '../../utils';
import {
    BlueArrow,
    Delete,
    Edit,
    FeedbackCross,
    GreenPlus,
    IconHover1,
    IconHover2,
    DocImage,
    UploadIcon,
    CloseIcon,
    ZipImage,
} from '../../assets/svg';
import { ImageWithFallback } from './common/ImageWithFallback';
import { ButtonComponent } from './Buttons/ButtonComponent';
import {
    DATE_TIME_FORMATS,
    DOCUMENTS,
    DOC_REQUEST,
    MAX_LENGTH,
    SYMBOL,
    REQUEST_STATUS,
    QUERY_KEYS,
    DOCUMENT_REQUEST_FORM,
    isCSMRole,
} from '../../constants';
import {
    setFileViewModalDetails,
    setLoaderProgress,
    setLoaderVisibility,
    setShowProgress,
} from '../../redux';
import { TextArea } from '../Admin/DocumentRequest/TextArea';
import { DeleteConfirmationModal } from './Modal/DeleteConfirmationModal';
import { formatDate } from '../../utils/common/formatHelpers';
import CircleSpinner from '../../assets/svg/CircleSpinner';
import { getDocumentRequestFields, updateDocumentRequestFields } from '../../utils/apiServices/documentRequestAPI';

const ADMIN_FILE_LIMIT = 150;
const USER_FILE_LIMIT = 5;
const BATCH_SIZE = 10;

export const DocumentDetailsTab = ({ setDocumentTabOpen, setSelectedRowIndex, tabData = {} }) => {
    const scrollBar = useRef(null);
    const queryClient = useQueryClient();
    const [editingNotes, setEditingNotes] = useState(false);
    const [uploadMore, setUploadMore] = useState(false);

    const [editingStatus, setEditingStatus] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [fileNames, setFileNames] = useState([]);
    const [notes, setNotes] = useState('');
    const [status, setStatus] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);
    // Required fields state
    const [reqFields, setReqFields] = useState([]);
    const [reqFieldValues, setReqFieldValues] = useState({});
    const [reqFieldsLoading, setReqFieldsLoading] = useState(false);
    const [reqFieldsSaving, setReqFieldsSaving] = useState(false);

    const dispatch = useDispatch();
    const isAdmin = useSelector((state) => state.user.isUserAdmin);
    const actualRole = useSelector((state) => state.user.role);

    const documentRequestQuery = useQuery({
        queryKey: [QUERY_KEYS.DOCUMENTS, tabData.id],
        queryFn: () => getDocumentDetailsByID({ id: tabData.id }),
        enabled: !!tabData.id,
        select: (data) => data?.data || {},
        staleTime: 0,
    });
    if (documentRequestQuery.isError) popup('error', documentRequestQuery.error.message);

    const documentUploadInfoQuery = useQuery({
        queryKey: [QUERY_KEYS.DOCUMENT_UPLOAD, tabData.id],
        queryFn: () => getFileUploadsByDocumentRequestID({ documentRequestID: tabData.id }),
        enabled: !!tabData.id,
        select: (data) => data?.data || {},
        staleTime: 0,
    });
    if (documentUploadInfoQuery.isError) popup('error', documentUploadInfoQuery.error.message);

    // Fetch required fields for this document request
    useEffect(() => {
        const run = async () => {
            if (!tabData?.id) return;
            try {
                setReqFieldsLoading(true);
                const res = await getDocumentRequestFields({ id: tabData.id });
                const rows = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
                setReqFields(rows);
                const initial = {};
                for (const f of rows) {
                    if (f?.value_json != null) initial[f.field_key] = f.value_json;
                    else if (typeof f?.value_text === 'string') initial[f.field_key] = f.value_text;
                    else initial[f.field_key] = '';
                }
                setReqFieldValues(initial);
            } catch (_) {
                // non-fatal
            } finally {
                setReqFieldsLoading(false);
            }
        };
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabData?.id]);

    const updateDocsRequestMutation = useMutation({
        mutationFn: async (obj) => await updateDocsRequest({ id: tabData.id, body: obj }),
        onSuccess: (res, obj) => {
            if (obj.notes !== undefined) {
                setEditingNotes(false);
                popup('success', DOC_REQUEST.NOTES_UPDATED);
            } else {
                setEditingStatus(false);
                popup('success', DOC_REQUEST.STATUS_UPDATED);
            }
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.DOCUMENTS],
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.DOCUMENT_UPLOAD, tabData.id],
            });
        },
        onError: (err) => {
            popup('error', err.message);
        },
    });

    const handleNotesSave = (obj = {}) => {
        if (!editingNotes && !editingStatus) return;
        updateDocsRequestMutation.mutate(obj);
    };

    useEffect(() => {
        if (documentRequestQuery.data?.notes) setNotes(documentRequestQuery.data.notes);
        if (documentRequestQuery.data?.status) setStatus(documentRequestQuery.data.status);
    }, [documentRequestQuery.data?.notes, documentRequestQuery.data?.status]);

    // to handle approve logic
    const handleApproveClick = async () => {
        try {
            await updateDocsRequest({ id: tabData.id, body: { notes, status: 'approved' } });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCUMENTS], exact: false });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCUMENT_UPLOAD, tabData.id] });
            popup('success', DOC_REQUEST.DOCUMENT_REQUEST_APPROVED);
        } catch (error) {
            popup('error', error.message);
        }
    };

    // to handle reject logic
    const handleRejectClick = async () => {
        try {
            await updateDocsRequest({ id: tabData.id, body: { notes, status: 'rejected' } });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCUMENTS], exact: false });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCUMENT_UPLOAD, tabData.id] });
            popup('success', DOC_REQUEST.DOCUMENT_REQUEST_REJECTED);
        } catch (error) {
            popup('error', error.message);
        }
    };

    const deleteDocumentRequestMutation = useMutation({
        mutationFn: async () => await deleteDocsRequest({ id: tabData.id }),
        onSuccess: () => {
            popup('error', DOC_REQUEST.DOCUMENT_REQUEST_DELETED);
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCUMENTS], exact: false });
            setDocumentTabOpen(false);
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const applySelectedFiles = (files) => {
        const fileLimit =
            isAdmin && documentUploadInfoQuery.data?.csm_upload_count > 0
                ? ADMIN_FILE_LIMIT - documentUploadInfoQuery.data.csm_upload_count
                : ADMIN_FILE_LIMIT;
        validateFiles(
            files,
            selectedFiles,
            DOCUMENT_REQUEST_FORM.FILE.FILE_SIZE_LIMIT_25MB,
            isAdmin ? fileLimit : USER_FILE_LIMIT,
        );
        const selectedFilesArray = Array.from(files);
        const selectedFileNames = selectedFilesArray.map((file) => file.name);
        setSelectedFiles((prevSelectedFiles) => [...prevSelectedFiles, ...selectedFilesArray]);
        setFileNames((prevFileNames) => [...prevFileNames, ...selectedFileNames]);
    };

    const onFileChange = (event) => {
        try {
            applySelectedFiles(event.target.files);
        } catch (error) {
            popup('error', error.message);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            try { applySelectedFiles(e.dataTransfer.files); } catch (error) { popup('error', error.message); }
            try { e.dataTransfer.clearData(); } catch (err) { console.warn('drag data clear failed', err); }
        }
    };
    const onDragOver = (e) => { e.preventDefault(); };

    // to handle upload file logic
    const openFileExplorer = () => {
        const fileLimit =
            isAdmin && documentUploadInfoQuery.data?.csm_upload_count > 0
                ? ADMIN_FILE_LIMIT - documentUploadInfoQuery.data.csm_upload_count
                : ADMIN_FILE_LIMIT;
        if (selectedFiles.length >= fileLimit && isAdmin) {
            popup('error', `Maximum ${ADMIN_FILE_LIMIT} files allowed`);
            return;
        }
        if (selectedFiles.length >= USER_FILE_LIMIT && !isAdmin) {
            popup('error', `Maximum ${USER_FILE_LIMIT} files allowed`);
            return;
        }
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true; // Allow multiple file selection
        input.onchange = onFileChange;
        input.click();
    };

    // to handle remove file logic
    const handleRemoveFile = (indexToRemove) => {
        setSelectedFiles((prevSelectedFiles) =>
            prevSelectedFiles.filter((_, index) => index !== indexToRemove),
        );
        setFileNames((prevFileNames) =>
            prevFileNames.filter((_, index) => index !== indexToRemove),
        );
    };

    const handleFileUpload = async () => {
        const fileCount = selectedFiles.length;
        const data = {
            account_id: tabData.account_id,
            doc_req_id: tabData.id,
            objects: fileNames,
        };
        const uploadedFileNames = [];
        const uploadedFilePaths = [];
        try {
            dispatch(setLoaderVisibility(true));
            dispatch(setShowProgress(true));

            // Get signed URL
            const response = await signedURL({ data });
            const urlData = response.data;
            let totalUploadedBytes = 0;
            let totalBytes = 0;

            for (const file of selectedFiles) {
                totalBytes += file.size;
            }

            const uploadFileWithProgress = (file, urlInfo) => {
                return new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open('PUT', urlInfo.preSignedUrl, true);
                    xhr.setRequestHeader('Content-Type', urlInfo.contentType);

                    let lastLoadedFileSize = 0;
                    xhr.upload.onprogress = (e) => {
                        if (e.lengthComputable) {
                            const delta = e.loaded - lastLoadedFileSize;
                            lastLoadedFileSize = e.loaded;

                            totalUploadedBytes += delta;
                            const percent = Math.round((totalUploadedBytes / totalBytes) * 100);
                            dispatch(setLoaderProgress(percent));
                        }
                    };
                    xhr.onload = () => {
                        if (xhr.status === 200) {
                            uploadedFilePaths.push(urlInfo.filePath);
                            uploadedFileNames.push(file.name);
                            resolve();
                        } else {
                            reject(new Error(`Upload failed for file ${file.name}`));
                        }
                    };
                    xhr.onerror = () => reject(new Error(`XHR error on file ${file.name}`));
                    xhr.send(file);
                });
            };

            const fileChunks = chunk(selectedFiles, BATCH_SIZE);
            const urlChunks = chunk(urlData, BATCH_SIZE);
            for (let i = 0; i < fileChunks.length; i++) {
                const batch = fileChunks[i];
                const batchUrls = urlChunks[i];
                await Promise.all(
                    batch.map((file, index) => uploadFileWithProgress(file, batchUrls[index])),
                );
            }

            const body = {
                document_request_id: tabData.id,
                document_category_id: tabData.document_category_id,
                file_path: uploadedFilePaths,
                file_name: uploadedFileNames,
            };
            await doFileUploadEntry({ body });
            popup(
                'success',
                fileCount > 1
                    ? `${fileCount} ${DOC_REQUEST.DOCS_UPLOADED}`
                    : `${fileCount} ${DOC_REQUEST.DOC_UPLOADED}`,
            );
            if (uploadMore) {
                setUploadMore(false);
            }
            setSelectedFiles([]);
            setFileNames([]);
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCUMENT_UPLOAD, tabData.id] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCUMENTS, tabData.id] });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.DOCUMENTS],
                exact: false,
            });
        } catch (error) {
            popup('error', error.message);
        } finally {
            dispatch(setLoaderVisibility(false));
        }
    };

    const fetchFileSignedUrl = async ({ filePath }) => {
        try {
            const response = await getFileSignedUrl({ filePath });
            return response.data.url;
        } catch (error) {
            popup('error', error.message);
        }
    };

    function getFileNameFromFilePath(filePath) {
        const parts = filePath.split('/');
        const fileNameWithExtension = parts[parts.length - 1];

        return fileNameWithExtension;
    }

    const handleViewFile = async (filePath) => {
        try {
            dispatch(setLoaderVisibility(true));
            dispatch(setShowProgress(true));
            const extension = getFileExtension(filePath);
            const fileType = getContentTypeByExtension(extension);
            const url = await fetchFileSignedUrl({ filePath: filePath });

            if (isDocORExcelExtension(extension)) {
                dispatch(
                    setFileViewModalDetails({
                        openFileViewerModal: true,
                        signedURL: url,
                        fileName: getFileNameFromFilePath(filePath),
                    }),
                );
            } else {
                const response = await fetch(url); // Fetch the file
                if (!response.ok) throw new Error('Failed to fetch file');

                const contentLength = response.headers.get('content-length');
                if (!contentLength) throw new Error('Unable to determine file size');

                const totalSize = parseInt(contentLength, 10);
                let loadedSize = 0;

                const reader = response.body.getReader();
                const chunks = [];

                // eslint-disable-next-line no-constant-condition
                while (true) {
                    const { done, value } = await reader.read();

                    if (done) {
                        break;
                    }

                    // Append the chunk to the list of chunks
                    chunks.push(value);
                    loadedSize += value.length;

                    // Calculate the progress percentage
                    const progress = Math.round((loadedSize / totalSize) * 100);

                    // Dispatch the progress to the state or UI
                    dispatch(setLoaderProgress(progress));
                }

                const blob = new Blob(chunks, { type: fileType });
                const uri = URL.createObjectURL(blob);

                dispatch(
                    setFileViewModalDetails({
                        openFileViewerModal: true,
                        docs: [{ uri, fileType }],
                        fileName: getFileNameFromFilePath(filePath),
                    }),
                );
            }
        } catch (error) {
            popup('error', error.message);
        } finally {
            dispatch(setLoaderVisibility(false));
            dispatch(setShowProgress(false));
        }
    };

    async function fetchBlobFromSignedUrl(signedURL, fileType) {
        try {
            const response = await fetch(signedURL);
            if (!response.ok) {
                throw new Error(DOC_REQUEST.ERROR_FETCHING_FILE);
            }
            const contentLength = response.headers.get('content-length');
            if (!contentLength) throw new Error('Unable to determine file size');

            const totalSize = parseInt(contentLength, 10);
            let loadedSize = 0;

            const reader = response.body.getReader();
            const chunks = [];

            // eslint-disable-next-line no-constant-condition
            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    break;
                }

                // Append the chunk to the list of chunks
                chunks.push(value);
                loadedSize += value.length;

                // Calculate the progress percentage
                const progress = Math.round((loadedSize / totalSize) * 100);

                // Dispatch the progress to the state or UI
                dispatch(setLoaderProgress(progress));
            }

            const blob = new Blob(chunks, { type: fileType });
            const uri = URL.createObjectURL(blob);

            return uri;
        } catch (error) {
            return null;
        }
    }

    const handleDownloadFile = async (filePath) => {
        dispatch(setLoaderVisibility(true));
        dispatch(setShowProgress(true));
        try {
            const signedURL = await fetchFileSignedUrl({ filePath });
            const extension = getFileExtension(filePath);
            const fileType = getContentTypeByExtension(extension);
            let blobURL = await fetchBlobFromSignedUrl(signedURL, fileType);
            // const blobURL = URL.createObjectURL(blob);

            // Create a temporary anchor element to initiate the download
            const downloadAnchor = document.createElement('a');
            downloadAnchor.href = blobURL;
            downloadAnchor.setAttribute('download', filePath.split('/').pop()); // Set the filename for the downloaded file
            downloadAnchor.style.display = 'none';
            document.body.appendChild(downloadAnchor);

            // Initiate the download
            downloadAnchor.click();

            // Cleanup
            document.body.removeChild(downloadAnchor);
            URL.revokeObjectURL(blobURL); // Revoke the blob URL to release memory
            dispatch(setLoaderVisibility(false));
        } catch (error) {
            popup('error', DOC_REQUEST.ERROR_DOWNLOADING_FILE);
            dispatch(setLoaderVisibility(false));
        }
    };

    const UploadDocumentSection = () => {
        // Compute unmet required fields in this scope for upload button gating
        const unmetRequiredFields = Array.isArray(reqFields)
            ? reqFields.filter((f) => f?.required && !String((reqFieldValues?.[f.field_key] ?? '')).trim())
            : [];
        return (
            <>
                <div className='doc-upload-add'>
                    <div
                        onDragOver={onDragOver}
                        onDrop={onDrop}
                        role='button'
                        tabIndex={0}
                        onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') openFileExplorer(); }}
                        aria-label='Drop files here or press Enter to select files'
                        style={{ border: '2px dashed #cbd5e1', borderRadius: 8, padding: 12, marginBottom: 12 }}
                    >
                        <span style={{ color: '#475569', fontSize: 13 }}>Drag and drop files here, or press Enter to browse</span>
                    </div>
                    <div className='filled-doc d-flex flex-wrap align-items-center justify-content-start'>
                        {selectedFiles.map((file) => {
                            return (
                                <div
                                    key={file.name}
                                    className='d-flex flex-column align-items-start justify-content-start'
                                >
                                    <div className='doc-hov position-relative'>
                                        <div className=' position-relative'>
                                            <DocImage />
                                        </div>
                                        <button
                                            className='cross-icon'
                                            id='action-icon'
                                            type='button'
                                            onClick={() =>
                                                handleRemoveFile(selectedFiles.indexOf(file))
                                            }
                                            title='Remove'
                                        >
                                            <FeedbackCross />
                                        </button>
                                        {file.name.length < 10 ? (
                                            <span title={file.name} className='mt-2'>
                                                {file.name}
                                            </span>
                                        ) : (
                                            <span className='truncate-text mt-2' title={file.name}>
                                                {file.name.slice(0, 4) +
                                                    '...' +
                                                    file.name.split('.')[1]}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        <span title='Upload document'>
                            <ButtonComponent
                                disabled={
                                    isAdmin
                                        ? selectedFiles.length >= ADMIN_FILE_LIMIT
                                        : selectedFiles.length >= USER_FILE_LIMIT
                                }
                                className={`p-0 add-btn-area d-flex align-items-center justify-content-center ssc-primary-green-btn ${selectedFiles.length >= 5 ? 'disabled' : ''}`}
                                type='button'
                                clickHandler={openFileExplorer}
                                aria-label='Upload document'
                            >
                                <UploadIcon stroke={'white'} />
                            </ButtonComponent>
                        </span>
                    </div>
                </div>

                {selectedFiles.length ? (
                    <ButtonComponent
                        className='upload-file w-auto upload-btn d-flex align-items-center justify-content-center ssc-primary-green-btn'
                        clickHandler={handleFileUpload}
                        disabled={!isAdmin && unmetRequiredFields.length > 0}
                        aria-disabled={!isAdmin && unmetRequiredFields.length > 0}
                        title={!isAdmin && unmetRequiredFields.length > 0 ? 'Complete required information first' : undefined}
                    >
                        {selectedFiles.length > 1 ? DOCUMENTS.UPLOAD_FILES : DOCUMENTS.UPLOAD_FILE}
                    </ButtonComponent>
                ) : null}
            </>
        );
    };

    const UploadedDocumentSection = ({ fileData, canShowAddButton = false }) => {
        if (isEmpty(fileData)) return null;
        return (
            <div
                style={{ maxHeight: '520px' }}
                className='filled-doc flex-wrap d-flex align-items-start justify-content-start overflow-y-auto overflow-x-hidden'
            >
                {!isEmpty(fileData) &&
                    fileData?.length > 0 &&
                    fileData?.map((file) => {
                        const zipRegex = /\.zip$/i;
                        const isZip = zipRegex.test(file.file_name);
                        return (
                            <div
                                key={file.id}
                                className='d-flex flex-column align-items-start justify-content-start'
                            >
                                <div className='d-flex flex-column'>
                                    <p className='timing mb-0'>
                                        {formatDate(file.uploaded_at, DATE_TIME_FORMATS.DATE_ONLY)}
                                    </p>
                                    <p className='timing mb-0'>
                                        {formatDate(file.uploaded_at, DATE_TIME_FORMATS.TIME_12HRS)}
                                    </p>
                                    <div className='doc-hov position-relative mt-1'>
                                        <div className='position-relative'>
                                            {isZip ? (
                                                <ZipImage
                                                    className='doc-img '
                                                    width={71}
                                                    height={71}
                                                />
                                            ) : (
                                                <DocImage
                                                    className='doc-img '
                                                    width={67}
                                                    height={61}
                                                />
                                            )}
                                        </div>
                                        <button
                                            className='icon1'
                                            type='button'
                                            id='action-icon'
                                            onClick={() => {
                                                handleDownloadFile(file.file_path);
                                            }}
                                            title={'Download'}
                                        >
                                            <IconHover1 height={16} width={16} />
                                        </button>
                                        {!isZip && (
                                            <button
                                                className={'icon2'}
                                                type='button'
                                                id='action-icon'
                                                onClick={() => handleViewFile(file.file_path)}
                                                title={'View'}
                                            >
                                                <IconHover2 height={16} width={16} />
                                            </button>
                                        )}
                                    </div>
                                    {file?.file_name?.length < 10 ? (
                                        <span title={file.file_name} className='mt-2'>
                                            {file.file_name}
                                        </span>
                                    ) : (
                                        <span
                                            className='truncate-text mt-2'
                                            title={file?.file_name}
                                        >
                                            {file?.file_name?.slice(0, 4) +
                                                '...' +
                                                file?.file_name?.split('.')[1]}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                {documentRequestQuery.data?.status === 'rejected' && canShowAddButton && (
                    <span className={selectedFiles.length ? '' : 'mt-5'}>
                        {selectedFiles.length &&
                        documentRequestQuery.data?.user_upload_count > 3 ? (
                            <hr />
                        ) : null}
                        <UploadDocumentSection />
                    </span>
                )}
            </div>
        );
    };

    const RenderDocumentSection = () => {
        const hasUserUploads = documentUploadInfoQuery.data?.user_upload_count > 0;
        const heading = isAdmin
            ? DOCUMENTS.UPLOADED_BY_USER
            : hasUserUploads
              ? DOCUMENTS.UPLOADED_DOCUMENTS
              : DOCUMENTS.UPLOAD_DOCUMENTS;

        const component = (() => {
            if (isAdmin) {
                return hasUserUploads ? (
                    <UploadedDocumentSection
                        showDate={false}
                        fileData={documentUploadInfoQuery.data?.uploaded_by_user}
                    />
                ) : (
                    <p>Nothing is uploaded</p>
                );
            }

            return hasUserUploads ? (
                <UploadedDocumentSection
                    showDate={true}
                    fileData={documentUploadInfoQuery.data?.uploaded_by_user}
                    canShowAddButton={true}
                />
            ) : (
                <UploadDocumentSection />
            );
        })();

        const unmetRequiredFields = (() => {
            if (!reqFields || !reqFields.length) return [];
            return reqFields.filter((f) => f?.required && !String((reqFieldValues?.[f.field_key] ?? '')).trim());
        })();

        return (
            <>
                <div className='d-flex align-items-center justify-content-between'>
                    <span className='title mb-3'>{heading}</span>
                    {hasUserUploads && (
                        <span className='text-info mb-3'>
                            Files: {documentUploadInfoQuery.data?.user_upload_count}
                        </span>
                    )}
                </div>
                <div>
                    {reqFieldsLoading ? (
                        <div className='mb-3' style={{ color: '#64748b' }}>Loading required information…</div>
                    ) : (
                        reqFields?.length > 0 && (
                            <div className='mb-3' style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
                                <div className='d-flex align-items-center justify-content-between'>
                                    <span className='title mb-2'>Required Information</span>
                                    <ButtonComponent
                                        className='ssc-primary-green-btn'
                                        clickHandler={async () => {
                                            try {
                                                setReqFieldsSaving(true);
                                                await updateDocumentRequestFields({ id: tabData.id, values: reqFieldValues });
                                                popup('success', 'Information saved');
                                            } catch (e) {
                                                popup('error', e?.message || 'Failed to save');
                                            } finally {
                                                setReqFieldsSaving(false);
                                            }
                                        }}
                                        disabled={reqFieldsSaving}
                                    >
                                        {reqFieldsSaving ? 'Saving…' : 'Save'}
                                    </ButtonComponent>
                                </div>
                                <div className='d-flex flex-column gap-2'>
                                    {reqFields.map((f) => (
                                        <label key={f.id || f.field_key} className='d-flex flex-column' style={{ fontSize: 13, color: '#334155' }}>
                                            <span>
                                                {f.label || f.field_key}
                                                {f.required ? <span className='ms-1' aria-hidden='true' style={{ color: '#ef4444' }}>*</span> : null}
                                            </span>
                                            <input
                                                value={reqFieldValues?.[f.field_key] ?? ''}
                                                onChange={(e) => setReqFieldValues((prev) => ({ ...prev, [f.field_key]: e.target.value }))}
                                                aria-required={f.required ? 'true' : 'false'}
                                                aria-invalid={f.required && !String((reqFieldValues?.[f.field_key] ?? '')).trim() ? 'true' : 'false'}
                                                style={{ padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }}
                                            />
                                        </label>
                                    ))}
                                    {unmetRequiredFields.length > 0 && (
                                        <div role='alert' style={{ color: '#b91c1c', fontSize: 12 }}>
                                            Please complete all required fields before uploading.
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    )}
                    <div className='doc border-0'>{component}</div>
                </div>
            </>
        );
    };

    const onUploadMoreHandler = () => {
        const fileLimit = ADMIN_FILE_LIMIT - documentUploadInfoQuery.data.csm_upload_count;
        if (fileLimit) {
            setUploadMore(!uploadMore);
            setSelectedFiles([]);
            setFileNames([]);
        } else {
            popup('error', `Maximum ${ADMIN_FILE_LIMIT} files allowed`);
        }
    };

    const RenderDocumentCSMSection = () => {
        const hasCsmUploads = documentUploadInfoQuery.data?.csm_upload_count > 0;
        const uploadedByCsm = documentUploadInfoQuery.data?.uploaded_by_csm;
        const component = (() => {
            if (isAdmin) {
                return hasCsmUploads ? (
                    <UploadedDocumentSection fileData={uploadedByCsm} />
                ) : (
                    <UploadDocumentSection />
                );
            }

            return hasCsmUploads ? (
                <UploadedDocumentSection fileData={uploadedByCsm} />
            ) : (
                <p>Nothing is uploaded</p>
            );
        })();
        const heading = (() => {
            if (isAdmin) {
                return hasCsmUploads ? DOCUMENTS.UPLOADED_DOCUMENTS : DOCUMENTS.UPLOAD_DOCUMENTS;
            }
            return DOCUMENTS.UPLOADED_BY_CSM;
        })();

        return (
            <>
                <div className='d-flex align-items-center justify-content-between'>
                    <div className='d-flex align-items-center gap-3'>
                        <span className='title mb-3'>{heading}</span>
                        {isCSMRole(actualRole) && hasCsmUploads && (
                            <button
                                type='button'
                                id='action-icon'
                                title={uploadMore ? 'Cancel' : 'Upload More'}
                                onClick={onUploadMoreHandler}
                            >
                                <span className={`mb-3 ${uploadMore ? 'text-danger' : ''}`}>
                                    {uploadMore ? (
                                        <CloseIcon size={14} />
                                    ) : (
                                        <UploadIcon size={16} />
                                    )}
                                </span>
                            </button>
                        )}
                    </div>
                    {hasCsmUploads && (
                        <span className='text-info mb-3'>
                            Files: {documentUploadInfoQuery.data?.csm_upload_count}
                        </span>
                    )}
                </div>
                <div>
                    {uploadMore ? (
                        <UploadDocumentSection />
                    ) : (
                        <div className='doc border-0'>{component}</div>
                    )}
                </div>
            </>
        );
    };

    const handleScrolling = () => {
        handleScroll(setIsScrolling, scrollBar, 2000);
    };

    return (
        <>
            <div className='tab-open-area show'>
                {documentRequestQuery.isFetching || documentUploadInfoQuery.isFetching ? (
                    <CircleSpinner absolute size='3rem' />
                ) : (
                    <div className='position-relative ps-3'>
                        <button
                            className='close-btn bg-white'
                            onClick={() => {
                                setDocumentTabOpen(false);
                                setSelectedRowIndex(0);
                            }}
                        >
                            <BlueArrow />
                        </button>
                        <div
                            onScroll={handleScrolling}
                            className={`position-relative content-area px-4 py-3 ${isScrolling ? 'scrolling' : 'not-scrolling'}`}
                        >
                            {isAdmin && (
                                <>
                                    {!isEmpty(documentRequestQuery.data) &&
                                        documentUploadInfoQuery.data?.uploaded_by_user.length > 0 &&
                                        documentRequestQuery.data?.status === 'pending' && (
                                            <div className='approve-div-btn me-1'>
                                                <button
                                                    className='ssc-secondary-white-btn'
                                                    type='button'
                                                    onClick={handleApproveClick}
                                                >
                                                    {DOCUMENTS.APPROVE}
                                                </button>
                                                <button
                                                    className='ssc-secondary-red-btn'
                                                    type='button'
                                                    onClick={handleRejectClick}
                                                >
                                                    {DOCUMENTS.REJECT}
                                                </button>
                                            </div>
                                        )}
                                    <div className='delete-icon'>
                                        <button
                                            type='button'
                                            onClick={() => setIsDeleteModalOpen(true)}
                                            id='action-btn-red'
                                            title='Delete'
                                        >
                                            <Delete stroke='#ff004d' />
                                        </button>
                                    </div>
                                </>
                            )}
                            <div className='row'>
                                <h6>Document</h6>
                                <div className='col-6 left-side'>
                                    {/* CLIENT SIDE */}
                                    <div className='documents-area'>
                                        <RenderDocumentSection />
                                    </div>
                                    {/* CSM SIDE */}
                                    <div className='documents-area'>
                                        <RenderDocumentCSMSection />
                                    </div>
                                </div>
                                <div className='col-6 right-side'>
                                    <div className='d-flex align-items-start flex-column detail-area'>
                                        <div className='priority-set'>
                                            <p className='title'>{DOCUMENTS.PRIORITY}</p>
                                            <div className='text d-flex align-items-center'>
                                                <div
                                                    className={`priority ${documentRequestQuery.data?.priority}-priority`}
                                                >
                                                    {capitalize(
                                                        documentRequestQuery.data?.priority,
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <p className='title'>{DOCUMENTS.CUSTOMER}</p>
                                            <div className='text d-flex align-items-center'>
                                                {documentRequestQuery.data?.account_name
                                                    ? startCase(
                                                          documentRequestQuery.data?.account_name,
                                                      )
                                                    : DOC_REQUEST.NOT_ASSIGNED}
                                            </div>
                                        </div>
                                        <div>
                                            <p className='title'>{DOCUMENTS.CSM}</p>
                                            <div className='text d-flex align-items-center'>
                                                <ImageWithFallback
                                                    src={tabData?.csm_image}
                                                    alt='user-img'
                                                    width={'24'}
                                                    height={'24'}
                                                />
                                                {documentRequestQuery.data?.csm_name
                                                    ? documentRequestQuery.data?.csm_name
                                                    : DOC_REQUEST.NOT_ASSIGNED}
                                            </div>
                                        </div>
                                        <div>
                                            <p className='title'>{DOCUMENTS.HIRING_CLIENT}</p>
                                            <div className='text d-flex align-items-center'>
                                                {documentRequestQuery.data?.hiring_client_name
                                                    ? startCase(
                                                          documentRequestQuery.data
                                                              ?.hiring_client_name,
                                                      )
                                                    : DOC_REQUEST.NOT_ASSIGNED}
                                            </div>
                                        </div>
                                        <div>
                                            <p className='title'>{DOCUMENTS.PLATFORM}</p>
                                            <div className='text d-flex align-items-center'>
                                                {documentRequestQuery.data?.platform_name ? (
                                                    <span>
                                                        {documentRequestQuery.data?.platform_name}
                                                        <sup>{SYMBOL.REGISTERED}</sup>
                                                    </span>
                                                ) : documentRequestQuery.data?.platforms ? (
                                                    <div className='d-flex flex-column gap-1'>
                                                        {JSON.parse(
                                                            documentRequestQuery.data?.platforms,
                                                        ).map((data) => (
                                                            <span key={data?.id}>
                                                                {data?.platform_name}
                                                                <sup>{SYMBOL.REGISTERED}</sup>
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    DOC_REQUEST.NOT_ASSIGNED
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <p className='title'>
                                                {DOCUMENTS.STATUS}
                                                {isAdmin && (
                                                    <ButtonComponent
                                                        className={`background-color-transparent ${editingStatus ? 'save-notes-button mb-0 px-2' : 'border-0 p-0'}`}
                                                        type='button'
                                                        clickHandler={() =>
                                                            !editingStatus
                                                                ? setEditingStatus(true)
                                                                : handleNotesSave({ status })
                                                        }
                                                        isLoading={
                                                            updateDocsRequestMutation.isPending
                                                        }
                                                    >
                                                        {editingStatus ? (
                                                            'Save'
                                                        ) : !documentRequestQuery.data?.status ||
                                                          documentRequestQuery.data?.status ===
                                                              '' ? (
                                                            <GreenPlus className={'ms-1 mb-2'} />
                                                        ) : (
                                                            <Edit className={'ms-1 mb-2'} />
                                                        )}
                                                    </ButtonComponent>
                                                )}
                                                {editingStatus && (
                                                    <ButtonComponent
                                                        className={`background-color-transparent  btn-close border-0 p-0 ms-2 mb-2`}
                                                        type='button'
                                                        clickHandler={() => {
                                                            setEditingStatus(false);
                                                        }}
                                                    />
                                                )}
                                            </p>
                                            {!editingStatus && (
                                                <div className='status-set d-flex align-items-center'>
                                                    <div
                                                        className={`status ${tabData.status}-status`}
                                                    >
                                                        {documentRequestQuery.data?.status
                                                            ? startCase(
                                                                  documentRequestQuery.data?.status,
                                                              )
                                                            : DOC_REQUEST.NOT_ASSIGNED}
                                                    </div>
                                                </div>
                                            )}

                                            {editingStatus && (
                                                <select
                                                    className='select-box-new cursor-pointer'
                                                    type='button'
                                                    name='item-per-page'
                                                    id='itemPerPage'
                                                    value={status}
                                                    onChange={(e) => setStatus(e.target.value)}
                                                >
                                                    {REQUEST_STATUS.map((req) => (
                                                        <option key={req.name} value={req.value}>
                                                            {req.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                        <div className='w-100'>
                                            <span className='d-flex align-items-center'>
                                                <p className='title'>{DOCUMENTS.NOTES}</p>
                                                {isAdmin && (
                                                    <ButtonComponent
                                                        className={`background-color-transparent ${editingNotes ? 'save-notes-button mb-0 px-2' : 'border-0 p-0'}`}
                                                        type='button'
                                                        clickHandler={() =>
                                                            !editingNotes
                                                                ? setEditingNotes(true)
                                                                : handleNotesSave({ notes })
                                                        }
                                                    >
                                                        {editingNotes ? (
                                                            'Save'
                                                        ) : !documentRequestQuery.data?.notes ||
                                                          documentRequestQuery.data?.notes ===
                                                              '' ? (
                                                            <GreenPlus className={'ms-1 mb-2'} />
                                                        ) : (
                                                            <Edit className={'ms-1 mb-2'} />
                                                        )}
                                                    </ButtonComponent>
                                                )}

                                                {isAdmin && editingNotes && (
                                                    <ButtonComponent
                                                        className={`background-color-transparent  btn-close border-0 p-0 ms-2 mb-2`}
                                                        type='button'
                                                        clickHandler={() => {
                                                            setEditingNotes(false);
                                                        }}
                                                    />
                                                )}
                                            </span>
                                            <div className='text d-flex align-items-center w-100'>
                                                {editingNotes ? (
                                                    <TextArea
                                                        value={notes}
                                                        note={notes}
                                                        setNote={setNotes}
                                                        placeholder='Add Notes'
                                                        className={'notes-textarea w-100 p-2'}
                                                        rows={7}
                                                        maxLength={MAX_LENGTH.DOCUMENT_NOTES}
                                                    />
                                                ) : documentRequestQuery.data?.notes ? (
                                                    <span title={notes}>{notes}</span>
                                                ) : (
                                                    DOC_REQUEST.NO_NOTES_CREATED
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {isDeleteModalOpen && (
                <DeleteConfirmationModal
                    isDeleteModal={isDeleteModalOpen}
                    setIsDeleteModal={setIsDeleteModalOpen}
                    selectedItemId={tabData.id}
                    handleDeleteClick={() => deleteDocumentRequestMutation.mutate()}
                    deleteButtonName={DOC_REQUEST.DELETE_BUTTON}
                    isLoading={deleteDocumentRequestMutation.isPending}
                />
            )}
        </>
    );
};

// define prop types
DocumentDetailsTab.propTypes = {
    setDocumentTabOpen: PropTypes.func,
    setSelectedRowIndex: PropTypes.func,
    debouncedApiCallFromParent: PropTypes.func,
    tabData: PropTypes.object,
};
